import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Check, X, MapPin, ChevronUp, ChevronDown } from "lucide-react";
import api from "@/api";

const inputCls = "rounded-lg border border-clayline bg-cream px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-terracotta/60";
const emptyForm = { name: "", area: "", address: "", city: "Bengaluru", hours: "8 AM – 12 AM, all days" };

const OutletManager = () => {
  const [outlets, setOutlets] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = () =>
    api.get("/locations").then((r) => setOutlets(r.data)).catch(() => toast.error("Failed to load outlets"));

  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const startEdit = (o) => {
    setEditing(o.id);
    setAdding(false);
    setForm({ name: o.name, area: o.area || "", address: o.address || "", city: o.city || "Bengaluru", hours: o.hours || "" });
  };

  const move = async (index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= outlets.length) return;
    const next = [...outlets];
    [next[index], next[j]] = [next[j], next[index]];
    setOutlets(next);
    try {
      await api.post("/admin/locations/reorder", { ids: next.map((o) => o.id) });
      toast.success("Order saved");
    } catch {
      toast.error("Could not save order");
      load();
    }
  };

  const save = async () => {
    if (!form.name.trim() || !form.address.trim()) {
      toast.error("Outlet name and address are required");
      return;
    }
    try {
      if (adding) await api.post("/admin/locations", form);
      else await api.put(`/admin/locations/${editing}`, form);
      toast.success(adding ? "Outlet added" : "Outlet updated");
      setEditing(null);
      setAdding(false);
      setForm(emptyForm);
      load();
    } catch {
      toast.error("Save failed. Please try again.");
    }
  };

  const remove = async (o) => {
    if (!window.confirm(`Delete "${o.name}"?`)) return;
    try {
      await api.delete(`/admin/locations/${o.id}`);
      toast.success("Outlet deleted");
      load();
    } catch {
      toast.error("Delete failed. Please try again.");
    }
  };

  const cancel = () => {
    setEditing(null);
    setAdding(false);
    setForm(emptyForm);
  };

  const editForm = (
    <div className="bg-sand/60 border border-terracotta/50 rounded-xl p-4 grid sm:grid-cols-2 gap-3" data-testid="outlet-edit-form">
      <input placeholder="Outlet name (e.g. Chai Times — HSR)" value={form.name} onChange={set("name")} className={inputCls} data-testid="outlet-form-name" />
      <input placeholder="Area (e.g. HSR Layout)" value={form.area} onChange={set("area")} className={inputCls} data-testid="outlet-form-area" />
      <input placeholder="Full address" value={form.address} onChange={set("address")} className={`${inputCls} sm:col-span-2`} data-testid="outlet-form-address" />
      <input placeholder="City" value={form.city} onChange={set("city")} className={inputCls} data-testid="outlet-form-city" />
      <input placeholder="Hours" value={form.hours} onChange={set("hours")} className={inputCls} data-testid="outlet-form-hours" />
      <div className="sm:col-span-2 flex gap-3">
        <button onClick={save} data-testid="outlet-save-btn" className="rounded-full px-6 py-2.5 font-mono text-xs uppercase tracking-[0.15em] bg-terracotta text-cream hover:bg-charcoal transition-colors inline-flex items-center gap-2">
          <Check size={14} /> Save
        </button>
        <button onClick={cancel} data-testid="outlet-cancel-btn" className="rounded-full px-6 py-2.5 font-mono text-xs uppercase tracking-[0.15em] border border-clayline hover:border-charcoal transition-colors inline-flex items-center gap-2">
          <X size={14} /> Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div data-testid="outlet-manager">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/50">{outlets.length} outlets · use arrows to set display order</p>
        <button
          onClick={() => { setAdding(true); setEditing(null); setForm(emptyForm); }}
          data-testid="outlet-add-btn"
          className="rounded-full px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] bg-charcoal text-cream hover:bg-terracotta transition-colors inline-flex items-center gap-2"
        >
          <Plus size={14} /> Add Outlet
        </button>
      </div>
      {adding && <div className="mb-6">{editForm}</div>}
      <div className="space-y-4">
        {outlets.map((o, i) => (
          editing === o.id ? (
            <div key={o.id}>{editForm}</div>
          ) : (
            <div key={o.id} className="bg-cream border border-clayline rounded-2xl p-5 flex items-start gap-4 group" data-testid={`outlet-row-${o.id.slice(0, 8)}`}>
              <div className="flex flex-col shrink-0 -my-1">
                <button
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  data-testid={`outlet-up-${o.id.slice(0, 8)}`}
                  className="p-1.5 text-charcoal/50 hover:text-terracotta disabled:opacity-20 transition-colors"
                  aria-label={`Move ${o.name} up`}
                >
                  <ChevronUp size={16} />
                </button>
                <button
                  onClick={() => move(i, 1)}
                  disabled={i === outlets.length - 1}
                  data-testid={`outlet-down-${o.id.slice(0, 8)}`}
                  className="p-1.5 text-charcoal/50 hover:text-terracotta disabled:opacity-20 transition-colors"
                  aria-label={`Move ${o.name} down`}
                >
                  <ChevronDown size={16} />
                </button>
              </div>
              <span className="font-mono text-xs text-terracotta mt-1 shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <div className="flex-1 min-w-0">
                <h3 className="font-serif font-bold text-xl">{o.name}</h3>
                <p className="text-sm text-charcoal/60 mt-1 flex gap-2"><MapPin size={14} className="shrink-0 mt-0.5 text-terracotta" />{o.address}</p>
                <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-charcoal/45 mt-2">{o.area} · {o.city} · {o.hours}</p>
              </div>
              <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity shrink-0">
                <button onClick={() => startEdit(o)} data-testid={`outlet-edit-${o.id.slice(0, 8)}`} className="p-2 hover:text-terracotta transition-colors" aria-label={`Edit ${o.name}`}>
                  <Pencil size={15} />
                </button>
                <button onClick={() => remove(o)} data-testid={`outlet-delete-${o.id.slice(0, 8)}`} className="p-2 hover:text-red-600 transition-colors" aria-label={`Delete ${o.name}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
};

export default OutletManager;
